'use client';

import { useModal } from '@ebay/nice-modal-react';

import type { EntityType } from '@/components/ProfileHeader/types';
import { SubscribeEntityAlertModal } from '@/lib/components/modals/SubscribeEntityAlertModal';

export interface UseSubscribeEntityAlertParams {
  entityType: EntityType;
  /** One or more entity ids. Single entities pass a 1-element array. */
  entityIds: number[];
  /** Display name shown in the modal/advanced-alert chips. */
  name: string;
  /**
   * Optional per-id display names, for the multi-id case where the ids do not
   * share a single name (parent univers → its distinct sous-univers). Falls
   * back to `name` for any id missing from the map.
   */
  entityNamesById?: Record<number, string>;
}

/**
 * Shared quick-subscribe boilerplate for every entity action slot.
 *
 * Encapsulates the `useModal(SubscribeEntityAlertModal)` wiring plus the
 * `modal.show(...)` payload construction so the slot components only have to
 * decide how the trigger is presented (card vs pill button).
 *
 * @returns A `subscribe` callback that opens the quick-subscribe modal.
 */
export const useSubscribeEntityAlert = ({
  entityType,
  entityIds,
  name,
  entityNamesById,
}: UseSubscribeEntityAlertParams): { subscribe: () => void } => {
  const subscribeModal = useModal(SubscribeEntityAlertModal);

  const subscribe = (): void => {
    subscribeModal.show({
      entityType,
      entityIds,
      entityName: name,
      // Only include the per-id map when present, so single-entity callers keep
      // the exact same payload shape they had before.
      ...(entityNamesById ? { entityNamesById } : {}),
    });
  };

  return { subscribe };
};
